import type { Database } from "@/types/database.types";
import { useState, useEffect } from "react";
import { MoreHorizontal, Briefcase } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];

interface TaskTableProps {
  tasks: TaskWithDetailsRow[];
  onEdit: (task: TaskWithDetailsRow) => void;
  onMarkComplete: (task: TaskWithDetailsRow) => void;
  onDelete: (taskId: string) => void;
  projectId?: string;
  projectName?: string;
}

export function TaskTable({
  tasks,
  onEdit,
  onMarkComplete,
  onDelete,
  projectId,
  projectName,
}: TaskTableProps) {
  const [projectsMap, setProjectsMap] = useState<Record<string, string>>({});
  const [assignedUsersMap, setAssignedUsersMap] = useState<
    Record<string, { username: string | null; full_name: string | null; avatar_url: string | null }>
  >({});

  useEffect(() => {
    // Extract project names and assigned users from tasks_with_details view data
    // If tasks come from the view, they already have project_name and assigned user info
    const projectMap: Record<string, string> = {};
    const assignedMap: Record<
      string,
      { username: string | null; full_name: string | null; avatar_url: string | null }
    > = {};

    tasks.forEach((task: TaskWithDetailsRow) => {
      // Use project_name from view if available, otherwise fall back to fetching
      if (task.project_id && task.project_name) {
        projectMap[task.project_id] = task.project_name;
      }

      // Use assigned user info from view if available
      if (task.assigned_to && task.assigned_username) {
        assignedMap[task.assigned_to] = {
          username: task.assigned_username,
          full_name: task.assigned_full_name,
          avatar_url: task.assigned_avatar_url,
        };
      }
    });

    // If we have a single project and projectName is provided, use it
    const projectIds = [
      ...new Set(tasks.map((t) => t.project_id).filter((id): id is string => Boolean(id))),
    ];
    if (projectIds.length === 1 && projectName) {
      projectMap[projectIds[0]!] = projectName;
    }

    setProjectsMap(projectMap);
    setAssignedUsersMap(assignedMap);

    // If we still need to fetch project names (not in view data), do it
    const missingProjectIds = projectIds.filter(
      (id) => !projectMap[id] && (!projectName || id !== projectIds[0]),
    );
    if (missingProjectIds.length > 0) {
      supabase
        .from("projects")
        .select("id, name")
        .in("id", missingProjectIds)
        .then(({ data }) => {
          if (data) {
            const additionalMap: Record<string, string> = {};
            data.forEach((p) => {
              if (p.id) additionalMap[p.id] = p.name;
            });
            setProjectsMap((prev) => ({ ...prev, ...additionalMap }));
          }
        });
    }
  }, [tasks, projectName]);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getProjectName = (task: TaskWithDetailsRow): string => {
    const taskProjectId = task.project_id;
    if (!taskProjectId) return "";
    if (projectName && projectId && taskProjectId === projectId) return projectName;
    // Check if task has project_name from view
    if (task.project_name) return task.project_name;
    return projectsMap[taskProjectId] || "";
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const handleRowClick = (task: TaskWithDetailsRow) => {
    onEdit(task);
  };

  const handleCheckboxClick = (e: React.MouseEvent, task: TaskWithDetailsRow) => {
    e.stopPropagation();
    onMarkComplete(task);
  };

  if (tasks.length === 0) {
    return <div className="text-muted-foreground py-8 text-center">No tasks yet</div>;
  }

  return (
    <>
      <div className="space-y-0">
        {tasks.map((task, index) => {
          const isCompleted = task.status === "done";
          const hasHighPriority = task.priority === "high";

          return (
            <div key={task.id}>
              <div
                className="flex items-start gap-2 py-2 px-2 cursor-pointer hover:bg-accent/30 transition-colors"
                onClick={() => handleRowClick(task)}
              >
                {/* Left: Checkbox */}
                <button
                  type="button"
                  onClick={(e) => handleCheckboxClick(e, task)}
                  className="mt-0.5 shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  style={{
                    borderColor: isCompleted ? "#ef4444" : "#666",
                    backgroundColor: isCompleted ? "#ef4444" : "transparent",
                  }}
                >
                  {isCompleted && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>

                {/* Center: Task content */}
                <div className="flex-1 min-w-0">
                  {/* Task title with priority dot */}
                  <div className="flex items-center gap-2 mb-1">
                    {hasHighPriority && !isCompleted && (
                      <div className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                    )}
                    <h3
                      className={`font-medium ${
                        isCompleted ? "text-muted-foreground line-through" : "text-muted-foreground"
                      }`}
                    >
                      {task.title}
                    </h3>
                  </div>

                  {/* Metadata: Date, Project, and Assigned User */}
                  <div className="flex flex-row items-center gap-2">
                    {task.due_date && (
                      <div className="text-sm text-red-700">{formatDate(task.due_date)}</div>
                    )}
                    {getProjectName(task) && (
                      <div className="flex items-center gap-1 text-sm text-[#999999]">
                        <Briefcase className="w-3.5 h-3.5" />
                        <span>{getProjectName(task)}</span>
                      </div>
                    )}
                    {task.assigned_to &&
                      (assignedUsersMap[task.assigned_to] || task.assigned_username) && (
                        <div className="flex items-center gap-1">
                          <Avatar className="h-5 w-5">
                            <AvatarImage
                              src={
                                assignedUsersMap[task.assigned_to]?.avatar_url ||
                                task.assigned_avatar_url ||
                                undefined
                              }
                            />
                            <AvatarFallback className="text-xs">
                              {getInitials(
                                assignedUsersMap[task.assigned_to]?.full_name ||
                                  task.assigned_full_name ||
                                  assignedUsersMap[task.assigned_to]?.username ||
                                  task.assigned_username,
                              )}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                      )}
                  </div>
                </div>

                {/* Right: Date and Project (redundant) + Action Button */}
                <div className="flex items-center gap-3 shrink-0">
                  {/* Action Menu */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      {!isCompleted && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            onMarkComplete(task);
                          }}
                        >
                          Mark as Completed
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(task);
                        }}
                      >
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          if (confirm("Are you sure you want to delete this task?")) {
                            onDelete(task.id!);
                          }
                        }}
                        className="text-destructive focus:text-destructive"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              {index < tasks.length - 1 && <div className="h-px bg-border mx-4" />}
            </div>
          );
        })}
      </div>
    </>
  );
}